/**
 * NotificationsTab — Per-event email / SMS / in-app toggle matrix.
 *
 * Grid layout:
 *   Event row | Email switch | SMS switch | In-App switch
 *
 * Each toggle is independent so the auto-save would be safe; we still
 * keep a Save button to match the other tabs' pattern and to batch
 * network traffic on slow connections.
 */
import { CheckCircle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../shared/ui/Button';
import { Switch } from '../../../shared/ui/Switch';
import { cn } from '../../../shared/utils/cn';
import {
  useBusinessSettings,
  useUpdateBusinessSettings,
} from '../../../features/settings/useBusinessSettings';
import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_EVENTS,
  type NotificationPrefs,
} from '../../../features/settings/types';
import { SettingsCard } from './SettingsCard';

export function NotificationsTab(): JSX.Element {
  const { t } = useTranslation('settings');
  const settingsQuery = useBusinessSettings();
  const updateMutation = useUpdateBusinessSettings();
  const [justSaved, setJustSaved] = useState(false);

  const {
    handleSubmit,
    reset,
    control,
    formState: { isDirty, isSubmitting },
  } = useForm<NotificationPrefs>({
    defaultValues: settingsQuery.data?.notifications,
  });

  useEffect(() => {
    if (settingsQuery.data?.notifications) {
      reset(settingsQuery.data.notifications);
    }
  }, [settingsQuery.data?.notifications, reset]);

  const onSubmit = async (values: NotificationPrefs) => {
    setJustSaved(false);
    await updateMutation.mutateAsync({ notifications: values });
    reset(values);
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2500);
  };

  if (settingsQuery.isLoading || !settingsQuery.data) {
    return <LoadingSkeleton />;
  }

  const channelHeader = {
    email: t('notifications.channelEmail'),
    sms: t('notifications.channelSms'),
    push: t('notifications.channelPush'),
  } as const;

  return (
    <SettingsCard
      title={t('notifications.sectionTitle')}
      hint={t('notifications.sectionHint')}
      onSubmit={handleSubmit(onSubmit)}
      footer={
        <>
          {justSaved ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-biz-sm text-biz-success mr-auto"
            >
              <CheckCircle weight="regular" size={18} aria-hidden="true" />
              {t('common.saved')}
            </span>
          ) : null}
          <Button
            type="button"
            intent="secondary"
            onClick={() =>
              settingsQuery.data && reset(settingsQuery.data.notifications)
            }
            disabled={!isDirty || isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" intent="primary" loading={isSubmitting} disabled={!isDirty}>
            {isSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      <div
        role="table"
        aria-label={t('notifications.sectionTitle')}
        className="border border-biz-border rounded-biz-card overflow-hidden"
      >
        <div
          role="row"
          className={cn(
            'grid grid-cols-[1fr_repeat(3,80px)] items-center gap-biz-content-lg',
            'px-biz-content-lg py-biz-content',
            'bg-biz-muted text-biz-xs font-semibold uppercase tracking-wider text-biz-muted-foreground',
          )}
        >
          <span role="columnheader" />
          {NOTIFICATION_CHANNELS.map((ch) => (
            <span key={ch} role="columnheader" className="text-center">
              {channelHeader[ch]}
            </span>
          ))}
        </div>

        {NOTIFICATION_EVENTS.map((event) => (
          <div
            key={event}
            role="row"
            className={cn(
              'grid grid-cols-[1fr_repeat(3,80px)] items-center gap-biz-content-lg',
              'px-biz-content-lg py-biz-content-lg',
              'border-t border-biz-border',
            )}
          >
            <div role="cell" className="flex flex-col min-w-0">
              <span className="text-biz-base font-medium">
                {t(`notifications.events.${event}` as const)}
              </span>
              <span className="text-biz-xs text-biz-muted-foreground">
                {t(`notifications.events.${event}Hint` as const)}
              </span>
            </div>
            {NOTIFICATION_CHANNELS.map((channel) => (
              <div
                key={channel}
                role="cell"
                className="flex items-center justify-center"
              >
                <Controller
                  control={control}
                  name={`${event}.${channel}` as const}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      aria-label={`${t(`notifications.events.${event}` as const)} — ${channelHeader[channel]}`}
                    />
                  )}
                />
              </div>
            ))}
          </div>
        ))}
      </div>
    </SettingsCard>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div className="space-y-biz-content" aria-busy="true" aria-live="polite">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="h-14 rounded-biz-card bg-biz-muted animate-pulse"
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
